import QRCode from "react-qr-code";
import { useEffect, useRef, useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { useInvitationContext } from "../context/InvitationContext";
import { useBusinessContext } from "../../../context/BusinessContext";
import { useTenant } from "../../../hooks/useTenant";
import { NotificationService } from "../../../services/notification";
import { Button } from "../../../components/ui/Button";
import { HikPinPanel } from "./HikPinPanel";
import { useIsPhone } from "../../../hooks/useIsPhone";
import { PhoneStepShell } from "./AccessFormPhone";
import { PhonePassCreated } from "./NewPassPhoneSteps";
import { formatPhoneForDisplay } from "../../../helpers/phoneUtils";
import { PassPaymentLinkPanel } from "./PassPaymentLinkPanel";
import { awaitingPaymentActions, isAwaitingPaymentPass } from "../awaitingPayment";
import { useT, type MessageKey } from "../../../i18n/admin";

// SAM-683: menu actions keep their English id (it is what onAction hands the parent, which
// matches on it); only the rendered label is translated.
const ACTION_LABEL: Record<string, MessageKey> = {
  'Share link': 'invitation.action.shareLink',
  'Print': 'invitation.action.print',
  'Confirmed': 'invitation.action.confirm',
  'Check In': 'invitation.action.checkIn',
  'Check Out': 'invitation.action.checkOut',
  'Cancel': 'invitation.action.cancel',
};
import { useInvitationBaseUrl } from "../invitationLink";

// SAM-199: ported off baseui/styletron — this file no longer imports
// `baseui`. Same derived data (business/tenant/session lookups, status and
// invitation-type resolution, invitation link building) and the same action
// menu contract (`onAction`), only the presentation moved to Tailwind + the
// DS tokens. The three-dot menu (previously `StatefulPopover` + `StatefulMenu`)
// is now a small anchored panel that closes on outside click — same items,
// same `onItemSelect` behavior.

interface Props {
  onClose: () => void;
  invitationData?: any;
  isViewMode?: boolean;
  onAction?: (action: string) => void;
  /** SAM-944: the New pass flow shows step 7 of the phone wizard instead of the card. */
  phoneStep?: boolean;
}

export const ConfirmationView = ({ onClose, invitationData, isViewMode = false, onAction, phoneStep = false }: Props) => {
  const { invitation } = useInvitationContext();
  const { business } = useBusinessContext();
  const { tenant } = useTenant();
  const { t, fmt } = useT();
  const isPhone = useIsPhone();
  // TODO: Uncomment this when we have a way to get the access type and get the gate rules from the access type
  // const { data: accessTypeResponse } = useBusinessAccessType("1"); // Commented out to avoid unnecessary API calls

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const baseUrl = useInvitationBaseUrl();


  // Combine invitation context data with API response data for complete information
  const data = invitationData || invitation;

  // Handle the case where data might be an array (from API response) or an object (from context)
  const apiData = Array.isArray(data) ? data[0] : data;

  // Create combined data that includes context information for manual flow
  const actualData = {
    ...apiData,
    // Preserve context data for manual flow
    ...(invitation && {
      duration: invitation.duration || apiData?.duration,
      guests: invitation.guests || apiData?.guests,
      accessType: invitation.accessType || apiData?.accessType,
      location: invitation.location || apiData?.location,
    })
  };


  // Extraer información de cualquiera de las dos fuentes posibles
  const businessName = actualData?.business_name || actualData?.business?.name || '';
  const legacyCode = actualData?.legacy_code || '';
  // Improved accessCode extraction with more fallback options
  const accessCode = actualData?.code || actualData?.id || invitation?.code || invitation?.legacy_code || '';


  // Determinar el tipo de invitación basado en el endpoint utilizado
  const getInvitationType = () => {
    // First check the entity field if it exists
    if (actualData?.entity) {
      return actualData.entity === 'business_access' ? 'Business' : 'Residential';
    }

    // Check endpoint type from sessionStorage as fallback
    const endpointType = sessionStorage.getItem('invitationEndpointType');
    if (endpointType === 'business_access') {
      return 'Business';
    }

    // Check if this has business_access_type_id and no entity field
    if (actualData?.business_access_type_id && !actualData?.entity) {
      return 'Business';
    }

    // For regular invitations, check if it's targeting a business or residential
    const selectedDestination = actualData?.location?.estate_id?.[0];
    if (selectedDestination?.isBusiness) {
      return 'Business';
    }

    // Default to Residential for invitation endpoint
    return 'Residential';
  };

  const invitationType = getInvitationType();
  // SAM-1312 (U35): a pass just created for an event reads «Event» and the event's name, not «Business» and the business.
  const eventPassName = !isViewMode ? invitation?.selectedEvent?.name ?? '' : '';

  // Determinar el tipo de huésped (Guest Type) - usar type_name si está disponible
  const getGuestType = () => {
    // First check if we have the type_name directly
    if (actualData?.type_name) {
      return actualData.type_name;
    }

    // Check context data
    if (actualData?.accessType?.[0]?.label) {
      return actualData.accessType[0].label;
    }

    // Fallback to business_access_type_id mapping
    const typeId = actualData?.business_access_type_id;
    if (typeId) {
      switch (typeId) {
        case 1: return t('invitation.guestType.guest');
        case 2: return t('invitation.guestType.business');
        case 3: return t('invitation.guestType.vip');
        case 4: return t('invitation.guestType.event');
        default: return t('invitation.guestType.other', { id: typeId });
      }
    }

    return t('invitation.guestType.guest');
  };

  const guestType = getGuestType();

  // Get status information
  const getStatusInfo = () => {
    const businessStatusId = actualData?.business_access_status_id;
    const invitationStatusId = actualData?.status_id;
    const statusId = businessStatusId || invitationStatusId;

    switch (statusId) {
      case 1:
        return { name: t('invitation.status.pending'), bg: '#FEF3C7', color: '#92400E' };
      case 2:
        return { name: t('invitation.status.confirmed'), bg: '#D1FAE5', color: '#065F46' };
      case 3:
        return { name: t('invitation.status.verified'), bg: '#DBEAFE', color: '#1E40AF' };
      case 4:
        return { name: t('invitation.status.checkedIn'), bg: '#C7D2FE', color: '#3730A3' };
      case 5:
        return { name: t('invitation.status.checkedOut'), bg: '#FDE68A', color: '#D97706' };
      case 6:
        return { name: t('invitation.status.cancelled'), bg: '#FECACA', color: '#991B1B' };
      case 7:
        return { name: t('invitation.status.expired'), bg: '#F3F4F6', color: '#6B7280' };
      case 8:
        return { name: t('invitation.status.processing'), bg: '#E0E7FF', color: '#5B21B6' };
      default:
        return { name: t('invitation.status.unknown'), bg: '#F3F4F6', color: '#374151' };
    }
  };

  const statusInfo = getStatusInfo();
  // S-38: a cancelled invitation shows no QR.
  const isCancelled = Number(actualData?.business_access_status_id || actualData?.status_id) === 6;
  // SAM-1293: a pass waiting for its payment link gives no access yet: no QR, Share, Print or door PIN until it is paid.
  const awaitingPayment = invitationType === 'Business' && isAwaitingPaymentPass(actualData);
  const linkExpiresAt = (actualData?.payment_link as { expires_at?: string } | null | undefined)?.expires_at;

  // Get available menu options based on status and dates
  const getAvailableActions = () => {
    const businessStatusId = actualData?.business_access_status_id;
    const invitationStatusId = actualData?.status_id;
    const currentStatusId = businessStatusId || invitationStatusId;


    // Get end date for Check Out logic
    const endDate = actualData?.end_date || actualData?.duration?.endDate;
    const isEndDateInFuture = () => {
      if (!endDate) return false;
      try {
        const end = new Date(endDate);
        const now = new Date();
        return end > now;
      } catch {
        return false;
      }
    };

    const actions: Array<{ id: string }> = [];

    // Always include basic actions (S-38: a cancelled invitation has no link to share)
    // SAM-1293: a pass waiting for its payment link has nothing to share or print yet.
    if (Number(currentStatusId) !== 6 && !awaitingPayment) actions.push({ id: 'Share link' });
    if (!awaitingPayment) actions.push({ id: 'Print' });

    // Add status-specific actions
    switch (currentStatusId) {
      case 1: // Pending
        actions.push({ id: 'Confirmed' });
        actions.push({ id: 'Check In' });
        actions.push({ id: 'Cancel' });
        break;

      case 2: // Confirmed
        actions.push({ id: 'Check In' });
        actions.push({ id: 'Cancel' });
        break;

      case 3: // Verified
        actions.push({ id: 'Check In' });
        actions.push({ id: 'Cancel' });
        break;

      case 4: // Checked In
        actions.push({ id: 'Check Out' });
        actions.push({ id: 'Cancel' });
        break;

      case 5: // Checked Out
        // Only show Check In if end date is in the future
        if (isEndDateInFuture()) {
          actions.push({ id: 'Check In' });
        }
        actions.push({ id: 'Cancel' });
        break;

      default:
        // For unknown status, don't add any status actions. A pass waiting for its payment link can only be cancelled (SAM-1293).
        awaitingPaymentActions(awaitingPayment ? 'processing' : actualData?.status_name).forEach((id) => actions.push({ id }));
        break;
    }

    return actions;
  };

  // Extraer la fecha
  const startDate = actualData?.duration?.date || actualData?.start_date;

  // Obtener gate rules de forma segura
  const gateRules = (() => {
    // Return "all" directly to avoid unnecessary API calls
    // Check for gate_rules object in actualData first as fallback
    if (actualData?.gate_rules) {
      if (typeof actualData.gate_rules === 'object') {
        return actualData.gate_rules.external || actualData.gate_rules.internal || 'all';
      }
      return actualData.gate_rules;
    }

    return 'all';
  })();

  const getInvitationLink = () => {
    const code = accessCode;

    if (!code) return '';

    // Ensure we have a clean base URL without trailing slashes
    const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
    // Remove any duplicate #/access/ or #/invitation/ in the code if present
    const cleanCode = code.replace(/^#\/(access|invitation)\//, '');

    // Determine link type based on actual invitation type (same logic as getInvitationType)
    let linkPath = 'invitation'; // Default to residential

    // Check the entity field if it exists
    if (actualData?.entity) {
      linkPath = actualData.entity === 'business_access' ? 'access' : 'invitation';
    } else {
      // Check endpoint type from sessionStorage as fallback
      const endpointType = sessionStorage.getItem('invitationEndpointType');
      if (endpointType === 'business_access') {
        linkPath = 'access';
      } else if (actualData?.business_access_type_id && !actualData?.entity) {
        // If this has business_access_type_id and no entity field, it's business
        linkPath = 'access';
      } else {
        // For regular invitations, check if it's targeting a business
        const selectedDestination = actualData?.location?.estate_id?.[0];
        if (selectedDestination?.isBusiness) {
          linkPath = 'access';
        }
      }
    }

    const link = `${cleanBaseUrl}/#/${linkPath}/${cleanCode}`;
    return link;
  };

  const handleCopyLink = () => {
    const link = getInvitationLink();


    if (link) {
      navigator.clipboard.writeText(link);
      NotificationService.success(t('invitation.linkCopied'));
    } else {
      console.error('[handleCopyLink] No link generated - accessCode:', accessCode);
      NotificationService.error(t('invitation.linkCopyFailed'));
    }
  };

  const handleDone = async () => {
    // Note: SMS is handled automatically by backend based on invitation status
    // No need to send SMS manually from frontend
    onClose();
  };

  // SAM-944 / SAM-1171: step 7 at 375 px (canvas v256 x4550): who, status, what, Share pass + Done.
  if (isPhone && phoneStep && actualData && !isViewMode) {
    const g = actualData?.guests?.mainGuest;
    const mg = actualData?.main_guest;
    const name = (g ? `${g.firstName || ''} ${g.lastName || ''}` : `${mg?.name || ''} ${mg?.last_name || ''}`).trim();
    const phone = g?.phoneNumber || mg?.tel ? formatPhoneForDisplay(g?.phoneNumber || mg?.tel) : '';
    const start = actualData?.duration?.startDate || actualData?.duration?.date || actualData?.start_date;
    const end = actualData?.duration?.endDate || actualData?.end_date;
    const startD = start ? new Date(start) : null;
    const endD = end ? new Date(end) : null;
    const dates = !startD || isNaN(startD.getTime())
      ? t('invitation.date.notSpecified')
      : actualData?.duration?.mode === 'multiple' && endD && !isNaN(endD.getTime())
        ? `${fmt.date(startD)} - ${fmt.date(endD)}`
        : fmt.date(startD);
    const adults = actualData?.guests?.companions?.adults || 0;
    const kids = actualData?.guests?.companions?.childrens || 0;
    const persons = [
      adults ? t(adults === 1 ? 'booking.phone.adultN' : 'booking.phone.adultsN', { n: adults }) : '',
      kids ? t(kids === 1 ? 'booking.phone.childN' : 'booking.phone.childrenN', { n: kids }) : '',
    ].filter(Boolean).join(', ');
    const service = invitation?.selectedService;
    const serviceName = service?.name_en || service?.name || '';
    const rows = [
      { label: t('booking.phone.business'), value: businessName },
      { label: t('booking.access.service'), value: serviceName },
      { label: t('booking.phone.dates'), value: dates },
      { label: t('booking.phone.persons'), value: persons },
    ].filter((r) => r.value);
    const share = async () => {
      const link = getInvitationLink();
      if (link && typeof navigator.share === 'function') {
        try { await navigator.share({ url: link }); return; } catch { /* closed by the user: nothing to do */ }
      }
      handleCopyLink();
    };
    return (
      <PhoneStepShell step={7} onClose={handleDone}>
        <PhonePassCreated
          name={name}
          phone={phone}
          status={statusInfo.name}
          statusColors={{ bg: statusInfo.bg, color: statusInfo.color }}
          rows={rows}
          linkPanel={actualData?.payment_link?.pay_url ? <PassPaymentLinkPanel link={actualData.payment_link} sms={actualData.payment_link_sms} /> : undefined}
          onShare={share}
          onDone={handleDone}
        />
      </PhoneStepShell>
    );
  }

  // Si no hay datos, mostrar un mensaje
  if (!actualData) {
    return (
      <div className="flex flex-col justify-center items-center" style={{ height: 200 }}>
        <p>{t('invitation.noData')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      {/* QR Section — flat, centered, no box */}
      <div className="flex flex-col items-center w-full relative gap-2" style={{ paddingBottom: 16, paddingTop: 8 }}>
        {/* Three dots menu aligned to the right */}
        <div className="flex w-full justify-end relative" ref={menuRef}>
          <button
            type="button"
            aria-label={t('invitation.moreActions')}
            onClick={() => setMenuOpen((open) => !open)}
            className="flex items-center justify-center"
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--r-8)',
              background: 'transparent',
              color: 'var(--ink-500)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <MoreHorizontal size={16} />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0"
              style={{
                top: 36,
                minWidth: 150,
                background: '#fff',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-8)',
                boxShadow: 'var(--sh-2)',
                zIndex: 20000,
                overflow: 'hidden',
              }}
            >
              {getAvailableActions().map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    if (item.id === 'Share link') {
                      handleCopyLink();
                    } else if (item.id === 'Print') {
                      window.print();
                    } else if (onAction) {
                      onAction(item.id);
                    }
                  }}
                  className="w-full text-left"
                  style={{
                    padding: '8px 14px',
                    fontSize: 13,
                    color: 'var(--ink-700)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {ACTION_LABEL[item.id] ? t(ACTION_LABEL[item.id]) : item.id}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col items-center gap-2 w-full mx-auto" style={{ maxWidth: 140 }}>
          {!isCancelled && !awaitingPayment && (
            <>
          <div className="flex items-center justify-center w-full" style={{ background: '#fff' }}>
            <QRCode
              value={legacyCode || accessCode || 'no-code'}
              size={110}
              level="H"
              style={{
                width: '100%',
                height: '100%',
                maxWidth: '110px',
                maxHeight: '110px',
              }}
            />
          </div>
          <p
            className="text-center"
            style={{
              fontSize: 11,
              margin: 0,
              color: 'var(--ink-400)',
              letterSpacing: '0.3px',
              fontFamily: 'var(--mono)',
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {accessCode}
          </p>
          <div className="text-center" style={{ fontSize: 12, color: 'var(--ink-400)', letterSpacing: '0.3px' }}>
            {t('invitation.scanQr')}
          </div>
            </>
          )}
          {awaitingPayment && (
            <div data-testid="awaiting-payment" className="text-center" style={{ fontSize: 13, color: 'var(--ink-700)' }}>
              {linkExpiresAt
                ? t('invitation.detail.awaitingPaymentExpires', { when: `${fmt.date(linkExpiresAt)} · ${fmt.time(linkExpiresAt)}` })
                : t('invitation.detail.awaitingPayment')}
            </div>
          )}
          <div
            className="text-center"
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: '4px 10px',
              borderRadius: 'var(--r-pill)',
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              backgroundColor: statusInfo.bg,
              color: statusInfo.color,
            }}
          >
            {statusInfo.name}
          </div>
        </div>
      </div>

      <div style={{ width: '100%', height: 1, background: 'var(--line-subtle)', margin: '6px 0' }} />

      <div className="flex flex-col gap-2">
        <div className="flex flex-col">
          <h3 style={{ fontSize: 13, fontWeight: 500, margin: '0 0 1px 0', color: 'var(--ink-900)' }}>{t('invitation.field.type')}</h3>
          <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)' }}>{eventPassName ? t('booking.access.event') : invitationType === 'Business' ? t('booking.kind.business') : t('booking.kind.residential')}</p>
        </div>

        <div style={{ width: '100%', height: 1, background: 'var(--line-subtle)', margin: '6px 0' }} />

        <div className="flex flex-col">
          <h3 style={{ fontSize: 13, fontWeight: 500, margin: '0 0 1px 0', color: 'var(--ink-900)' }}>{t('invitation.field.guestType')}</h3>
          <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)' }}>{guestType}</p>
        </div>

        <div style={{ width: '100%', height: 1, background: 'var(--line-subtle)', margin: '6px 0' }} />

        <div className="flex flex-col">
          <h3 style={{ fontSize: 13, fontWeight: 500, margin: '0 0 1px 0', color: 'var(--ink-900)' }}>{t('invitation.field.whereTo')}</h3>
          <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)' }}>
            {eventPassName
              ? eventPassName
              : invitationType === 'Residential' && actualData?.location?.estate_id?.[0]?.label
                ? actualData.location.estate_id[0].label
                : businessName}
          </p>
        </div>

        <div style={{ width: '100%', height: 1, background: 'var(--line-subtle)', margin: '6px 0' }} />

        <div className="flex flex-col">
          <h3 style={{ fontSize: 13, fontWeight: 500, margin: '0 0 1px 0', color: 'var(--ink-900)' }}>{t('invitation.field.when')}</h3>
          <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)' }}>
            {(() => {
              // Get dates from context or API data
              // For multiple days, duration.date is undefined, so check duration.startDate first
              const startDate = actualData?.duration?.startDate || actualData?.duration?.date || actualData?.start_date;
              const endDate = actualData?.duration?.endDate || actualData?.end_date;

              if (!startDate) return t('invitation.date.notSpecified');

              try {
                const start = new Date(startDate);
                const end = endDate ? new Date(endDate) : null;

                // Check if start date is valid
                if (isNaN(start.getTime())) {
                  console.error('Invalid start date:', startDate);
                  return t('invitation.date.invalid');
                }

                const today = new Date();

                // Format a single date
                const formatDate = (date: Date) => {
                  const isToday = date.toDateString() === today.toDateString();

                  if (isToday) return t('booking.howLong.today');

                  return fmt.date(date);
                };

                const formattedStart = formatDate(start);

                // If we have an end date, show range
                if (end && !isNaN(end.getTime())) {
                  const formattedEnd = formatDate(end);
                  return `${formattedStart} - ${formattedEnd}`;
                }

                // Otherwise just show start date
                return formattedStart;
              } catch (error) {
                console.error('Error formatting dates:', error);
                return t('invitation.date.notAvailable');
              }
            })()}
          </p>
        </div>

        <div style={{ width: '100%', height: 1, background: 'var(--line-subtle)', margin: '6px 0' }} />

        {/* Sección de Guests - 2 Column Layout */}
        <div className="flex flex-col gap-2">
          <h3 style={{ fontSize: 13, fontWeight: 500, margin: '0 0 1px 0', color: 'var(--ink-900)' }}>{t('invitation.field.guest')}</h3>

          {/* 2-Column Grid */}
          <div className="flex flex-col gap-2">
            {/* Main Guest Row */}
            <div className="flex justify-between items-center">
              <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', fontWeight: 500 }}>{t('invitation.field.mainGuest')}</p>
              <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', textAlign: 'right' }}>
                {actualData?.main_guest?.name
                  ? `${actualData.main_guest.name} ${actualData.main_guest.last_name || ''}`
                  : actualData?.guests?.mainGuest?.firstName
                    ? `${actualData.guests.mainGuest.firstName} ${actualData.guests.mainGuest.lastName || ''}`
                    : ''}
              </p>
            </div>

            {/* Phone Number Row */}
            <div className="flex justify-between items-center">
              <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', fontWeight: 500 }}>{t('invitation.field.phone')}</p>
              <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', textAlign: 'right' }}>
                {formatPhoneForDisplay(actualData?.main_guest?.tel || actualData?.guests?.mainGuest?.phoneNumber) === '--' ? '' : formatPhoneForDisplay(actualData?.main_guest?.tel || actualData?.guests?.mainGuest?.phoneNumber)}
              </p>
            </div>

            {/* Email Row - Only show if email exists */}
            {(actualData?.main_guest?.email || actualData?.guests?.mainGuest?.email) && (
              <div className="flex justify-between items-center">
                <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', fontWeight: 500 }}>{t('invitation.field.email')}</p>
                <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', textAlign: 'right' }}>
                  {actualData?.main_guest?.email || actualData?.guests?.mainGuest?.email || ''}
                </p>
              </div>
            )}

            {/* Adults Row */}
            <div className="flex justify-between items-center">
              <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', fontWeight: 500 }}>{t('booking.howLong.adults')}</p>
              <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', textAlign: 'right' }}>
                {(() => {
                  const businessGuests = actualData?.business_guest || [];
                  const contextCompanions = actualData?.guests?.companions;

                  let totalAdults = 1; // Always include main guest

                  if (contextCompanions) {
                    totalAdults = contextCompanions.adults || 1;
                  } else if (Array.isArray(businessGuests)) {
                    totalAdults = 1 + businessGuests.length;
                  }

                  return totalAdults;
                })()}
              </p>
            </div>

            {/* Children Row */}
            <div className="flex justify-between items-center">
              <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', fontWeight: 500 }}>{t('booking.howLong.children')}</p>
              <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', textAlign: 'right' }}>
                {(() => {
                  const childrenCount = actualData?.children_count || 0;
                  const contextCompanions = actualData?.guests?.companions;

                  let totalChildren = childrenCount;

                  if (contextCompanions) {
                    totalChildren = contextCompanions.childrens || 0;
                  }

                  return totalChildren;
                })()}
              </p>
            </div>
          </div>
        </div>

        <div style={{ width: '100%', height: 1, background: 'var(--line-subtle)', margin: '6px 0' }} />

        <div className="flex flex-col">
          <h3 style={{ fontSize: 13, fontWeight: 500, margin: '0 0 1px 0', color: 'var(--ink-900)' }}>{t('invitation.field.gate')}</h3>
          <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)' }}>{String(gateRules).trim().toLowerCase() === 'all' ? t('invitation.gateAll') : gateRules}</p>
        </div>

        {/* SAM-307/306/426: door PIN, its generation status, effective end and uses. In view
            mode it shows whenever there is a PIN (as before); right after create it shows only
            when the backend sent hik_pin_status, so an older response looks exactly as today. */}
        {invitationData && !Array.isArray(invitationData) && !awaitingPayment && (
          <HikPinPanel row={invitationData} requireStatus={!isViewMode} />
        )}

        {/* SAM-1171: a pass paid by link shows the link right after create (it is answered only once). */}
        {!isViewMode && invitationData && !Array.isArray(invitationData) && (
          <PassPaymentLinkPanel link={invitationData.payment_link} sms={invitationData.payment_link_sms} />
        )}

        {isViewMode ? null : (
          <>
            <div style={{ width: '100%', height: 1, background: 'var(--line-subtle)', margin: '6px 0' }} />
            <Button variant="primary" className="w-full" onClick={handleDone}>
              {t('common.done')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
